import { VersionMismatchError } from '../errors/VersionMismatchError.js';

/**
 * A mapping of package names to their versions.
 */
let packageVersions: Record<string, string> = {};

/**
 * Timeout ID for batching version checks.
 */
let versionCheckTimeout: NodeJS.Timeout | null = null;

/**
 * Asserts that all `@dynamic-labs-sdk` packages are on the same version.
 * Throws an error with instructions if versions mismatch.
 *
 * @param {string} packageName - The name of the package to assert.
 * @param {string} version - The version of the package.
 */
export const assertPackageVersion = (
  packageName: string,
  version: string
): void => {
  // Update the package version mapping
  packageVersions[packageName] = version;

  // Clear any existing timeout
  if (versionCheckTimeout) {
    clearTimeout(versionCheckTimeout);
    versionCheckTimeout = null;
  }

  /**
   * Timeout is set to 100ms to ensure the following:
   * - All package versions have been imported and registered.
   */
  const timeout = 100;

  /**
   * Set a timeout to batch the version check.
   * This ensures that the check is executed after all package versions have been imported
   * and registered.
   */
  versionCheckTimeout = setTimeout(() => {
    const versions = Object.values(packageVersions);
    const [firstVersion] = versions;
    const allSameVersion = versions.every((v) => v === firstVersion);

    if (!allSameVersion) {
      // Determine the required (target) version
      const targetVersion =
        packageVersions['@dynamic-labs-sdk/client'] || firstVersion;

      const error = new VersionMismatchError(targetVersion, packageVersions);

      // eslint-disable-next-line no-console
      console.error(error);
    }

    versionCheckTimeout = null;
  }, timeout);
};

// Helper functions meant only for testing

export const clearPackageVersionState = (): void => {
  if (versionCheckTimeout) {
    clearTimeout(versionCheckTimeout);
  }

  packageVersions = {};
  versionCheckTimeout = null;
};

export const getVersionCheckTimeout = (): NodeJS.Timeout | null =>
  versionCheckTimeout;
